/* eslint-disable no-console */
import type { ISbStoryData } from "@storyblok/react";
import {
  getStoryblokApi,
  StoryblokComponent,
  useStoryblokState,
} from "@storyblok/react";
import type {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from "next";
import { NextSeo } from "next-seo";
import type { ParsedUrlQuery } from "querystring";

import type { Blok } from "@/bloks/Page";
import PLP from "@/components/PLP";
import { I18nHead } from "@/features/i18n/I18nHead";
import { fetchServerTranslations } from "@/i18n.server";
import getCategoryId from "@/lib/centra/getCategoryId";
import { getAllStories } from "@/lib/centra/useStories/getStories";
import { getSubcategories } from "@/lib/hooks/useSubcategories";
import getStory from "@/lib/storyblok/getStory";

type Retailer = ISbStoryData<{ country: string }>;
const ContentType = ({ story, staticProps }: any) => {
  if (story?.content?.component === "Category" || story === null) {
    return (
      <PLP
        story={story!}
        subcategories={staticProps?.subcategories}
        categoryIds={staticProps?.cId !== null ? staticProps.cId : undefined}
      />
    );
  }
  return <StoryblokComponent blok={story.content} restProps={staticProps} />;
};

const Info: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  story: initialStory,
  staticProps: staticProps,
  // preview,
}) => {
  const story = useStoryblokState(initialStory!, {});
  const seo = story?.content?.metadata;

  return (
    <>
      <I18nHead story={story} />

      {story?.content && (
        <NextSeo
          title={seo?.title || story?.name || ""}
          description={seo?.description || ""}
          openGraph={{
            title: seo?.og_title || seo?.title || "",
            description:
              seo?.og_description ||
              seo?.description ||
              (story.content.abstract as string) ||
              "",
            images: seo?.og_image ? [{ url: seo?.og_image }] : [{ url: "" }],
          }}
        />
      )}

      <ContentType story={story} staticProps={staticProps} />
    </>
  );
};

export default Info;

export const getStaticProps = async ({
  params,
  preview = false,
  locale = "en",
}: GetStaticPropsContext) => {
  let info = params?.info as string[];

  if (info && info[0] === "info-pages") {
    info = info.slice(1);
  }

  const slug = info ? (info as string[]).join("/") : "";

  let story = await getStory<Blok>({
    slug,
    preview,
    locale,
  });

  if (!story?.content) {
    story = await getStory<Blok>({
      slug,
      folder: "info-pages",
      preview,
      locale,
    });
  }
  if (!story?.content) {
    story = await getStory<Blok>({
      slug,
      folder: "category",
      preview,
      locale,
    });
  }
  const staticProps = {} as any;
  let category = false;

  if (story?.content?.component === "Category" || !story?.content) {
    if (!story?.content) {
      story = undefined;
    }
    const categoryArray = params?.info as string[];
    const subcategoriesPromise = getSubcategories(categoryArray, locale);
    const categoryIdPromise = getCategoryId(categoryArray, locale);
    const defaultCategoryIdPromise = getCategoryId(categoryArray, locale);

    const [subcategories, categoryId, defaultId] = await Promise.all([
      subcategoriesPromise,
      categoryIdPromise,
      defaultCategoryIdPromise,
    ]);
    if (subcategories) {
      staticProps.subcategories = subcategories;
    }
    const cId = categoryId || defaultId;
    if (cId) {
      staticProps.cId = [cId];
      category = true;
    }
  }

  if (story?.content?.body?.find((blok) => blok.component === "retailers")) {
    const { stories: initialRetailers } = await getAllStories<Retailer>(
      "retailer",
      preview
    );

    const filterField = "country";
    const options = (initialRetailers &&
      (initialRetailers
        .map(
          (story) =>
            (filterField in story?.content && story?.content[filterField]) || ""
        )
        .filter((value, index, self) => self.indexOf(value) === index)
        .filter(Boolean)
        .sort((a, b) => (a > b ? 1 : -1)) as string[])) ?? [""];
    story?.content?.body.forEach((blok, key) => {
      if (blok.component === "retailers") {
        const blokData = story?.content?.body?.[key];
        blokData.initialRetailers = initialRetailers;
        blokData.options = options;
        story.content.body[key] = blokData;
      }
    });
  }

  return {
    notFound: !story?.content && !category,
    props: {
      ...(await fetchServerTranslations(locale)),
      story,
      preview,
      staticProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({}) => {
  const storyBlokApi = getStoryblokApi();
  const { data } = await storyBlokApi.get("cdn/links", {
    starts_with: "info-pages/",
    version: "published",
  });
  const paths = [] as { params: ParsedUrlQuery; locale?: string }[];
  Object.keys(data.links).forEach((linkKey) => {
    if (data.links[linkKey].is_folder) {
      return;
    }
    const slug = data.links[linkKey].real_path.replaceAll("//", "/");
    // eslint-disable-next-line no-console
    const realSlug = (slug.startsWith("/") ? slug.slice(1) : slug).split("/");

    paths.push({ params: { info: [...realSlug] } });
  });

  return {
    paths,
    fallback: "blocking",
  };
};
